import { BreifInfluencerV2StatusEnum } from "@entities/brief/lib/constants";
import { useGetBriefInfluencerSearchFacets } from "@entities/brief/lib/hooks/useGetBriefInfluencerSearchFacets";
import { BriefTimelinePopOver } from "@features/briefTimeline/BriefTimelinePopOver";
import { IBriefInfluencerV2 } from "@shared/lib/types/briefInfluencerV2";
import { InputNumber } from "@shared/ui/InputNumber";
import SelectInput from "@shared/ui/SelectInput";
import { Form, FormInstance, Input, Space } from "antd";
import { useState } from "react";
import styles from "./styles.module.scss";

type PaymentTermsSelectFieldProps = {
  value?: string;
  onChange?: (value: string) => void;
  briefId: string;
};

function PaymentTermsSelectField({
  value,
  onChange,
  briefId,
}: PaymentTermsSelectFieldProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { searchFacets } = useGetBriefInfluencerSearchFacets(
    briefId,
    BreifInfluencerV2StatusEnum.All
  );

  return (
    <SelectInput
      placeholder="Select payment terms"
      allowClear
      showSearch
      onSearch={() => {}}
      value={value || ""}
      isFetching={false}
      dropdownOpen={dropdownOpen}
      setDropdownOpen={setDropdownOpen}
      options={
        searchFacets?.paymentTerms?.map((p) => ({
          label: p._id,
          value: p._id,
        })) || []
      }
      onchange={(selectedValue) => onChange?.((selectedValue as string) || "")}
      newItem={{
        placeholder: "Enter a new payment term",
        addNewItem: (item) => onChange?.(item),
        addButtonText: "Add payment term",
      }}
    />
  );
}

type AddCommercialsProps = {
  form: FormInstance;
  briefInfluencer: IBriefInfluencerV2;
};

export function AddCommercials({ form, briefInfluencer }: AddCommercialsProps) {
  const finalInfluencerCost = Form.useWatch("cost", form);
  const brandPrice = briefInfluencer?.brandCost?.cost ?? 0;
  const influencerPrice =
    finalInfluencerCost ?? briefInfluencer?.influencerCost?.cost ?? 0;
  const revenue = brandPrice - influencerPrice;
  const marginPercentage =
    brandPrice > 0 ? ((revenue / brandPrice) * 100).toFixed(2) : "0.00";

  return (
    <Form
      form={form}
      className={`${styles.commercialsForm} ${styles.commercialsCard}`}
      layout="vertical"
    >
      <p className={styles.cardLabel}>Commercials</p>
      <div className={styles.readOnlyGrid}>
        <Form.Item label="Last influencer commercials">
          <InputNumber
            placeholder="Commercials"
            className={styles.fullField}
            disabled
            value={briefInfluencer?.influencerCost?.cost}
          />
        </Form.Item>
        <Form.Item label="Brand cost">
          <InputNumber
            placeholder="Brand cost"
            className={styles.fullField}
            disabled
            value={briefInfluencer?.brandCost?.cost}
          />
        </Form.Item>
      </div>
      <Form.Item
        label={
          <Space>
            <BriefTimelinePopOver
              briefInfluencer={briefInfluencer}
              type="influencer"
            />
            Final influencer commercials
          </Space>
        }
        name="cost"
        rules={[{ required: true, message: "Enter the final commercials" }]}
      >
        <InputNumber placeholder="Enter amount" className={styles.fullField} />
      </Form.Item>
      <div className={styles.marginSummary}>
        <span className={styles.marginLabel}>Margin</span>
        <span className={styles.marginValue}>
          {revenue.toFixed(2)} ({marginPercentage}%)
        </span>
      </div>
      <Form.Item
        label="Payment terms"
        name="paymentTerms"
        rules={[{ required: true, message: "Please select payment terms" }]}
      >
        <PaymentTermsSelectField briefId={briefInfluencer?.briefId} />
      </Form.Item>
      <Form.Item
        label="Notes for the influencer"
        name="confirmationNotes"
      >
        <Input.TextArea
          rows={3}
          placeholder="Shared with the influencer in the confirmation email"
        />
      </Form.Item>
    </Form>
  );
}
